/**
 * OAuth consent screen (en).
 *
 * Every literal `@` is escaped as {'@'}. vue-i18n reads a bare `@` as the
 * start of a linked message, throws when the message is rendered, and blanks
 * the whole enclosing subtree with nothing in the browser console: it looks
 * like a styling bug, not a translation one. tests/locales/messageSyntax.spec.ts
 * compiles every message in here.
 */
export const oauth = {
  consent: {
    pageTitle: 'Authorise access',
    heading: 'Access to your ITSH account',
    intro: '"{client}" wants to act on your ITSH account on your behalf.',
    loading: 'Loading the request',
    expiresAt: 'This request expires on {date}.',
    trust: {
      title: 'We have not checked this application',
      body: 'Any application can register itself here, pick whatever name and address it likes, and pose as somebody else. We do not verify that. Only allow access if you started this connection yourself, just now.',
      levelLabel: 'Rating',
      levels: {
        unverified: 'Not verified',
        verified: 'Verified',
        first_party: 'ITSH'
      }
    },
    client: {
      sectionTitle: 'Application',
      name: 'Name',
      host: 'Verified host',
      hostNone: 'No verified host',
      hostNoneHint: 'This application registered itself. Its name says nothing about who operates it.',
      hostPlatformHint: 'This address sits on the ITSH platform but does not belong to us: any customer can publish an application there.',
      clientId: 'Client ID',
      resources: 'What the access is valid for',
      resourcesHint: 'The access is only accepted by these interfaces. Anywhere else it is worthless.',
      redirect: 'Where you are sent afterwards',
      redirectHint: 'We send you back to exactly this address, carrying an access code. Check it character by character.'
    },
    scopes: {
      sectionTitle: 'Permissions this application is asking for',
      hint: 'You can clear individual permissions. Anything you clear, the application does not get.',
      none: 'The application is not asking for any permission we recognise.',
      noneSelected: 'You cleared everything. That declines the request.',
      withheld: 'The mail permissions in this request ({scopes}) cannot be granted: your account is recorded as not needing a data processing agreement (DPA) because you entrust us with no personal data of third parties. Without a DPA we have no instruction to pass mailbox data to an application. Accept the DPA in the customer portal under "DPA", then you can grant these permissions.',
      unknownTitle: 'Unknown permission',
      unknownDescription: 'This build does not know this permission yet and cannot tell you what it allows.',
      billingPurchaseNote: 'To buy anything, the application also needs the "Billing: buy" permission. Without it, it buys nothing, whatever else you select here.',
      riskConfirm: 'I understand that the critical permissions above keep having effect even after I revoke this access.'
    },
    risk: {
      low: 'Read only',
      medium: 'Changes data',
      high: 'Critical'
    },
    areas: {
      account: 'Account',
      billing: 'Billing',
      dns: 'DNS',
      domain: 'Domains',
      handle: 'Domain contacts',
      hosting: 'Web hosting',
      k8s: 'Kubernetes',
      mail: 'Email',
      registry: 'Container registry',
      storage: 'Object storage',
      vserver: 'VServer'
    },
    levels: {
      read: 'Read',
      write: 'Change',
      delete: 'Delete',
      purchase: 'Buy',
      credentials: 'Credentials',
      exec: 'Run commands',
      console: 'Console',
      authcode: 'Auth code',
      transfer: 'Transfer'
    },
    descriptions: {
      account: {
        read: 'Reads your profile: name, address and account settings.',
        write: 'Changes your profile, for example your address or contact details.',
        credentials: "Adds SSH keys to your account and removes them, for example yourname{'@'}laptop."
      },
      billing: {
        read: 'Reads invoices, subscriptions and outstanding amounts.',
        write: 'Changes billing data, for example your subscription or the invoice address.',
        purchase: 'Allows spending money at all. How much is decided by the spend cap below.'
      },
      dns: {
        read: 'Reads your DNS zones and every record in them.',
        write: 'Creates, changes and deletes DNS records. That decides where your domains and your email point. It covers the records that decide which servers receive your email (MX) and which may send email in your name (SPF, DKIM, DMARC), and the ones that decide who can get certificates for your domains (CAA and _acme-challenge).',
        credentials: 'Creates DynDNS tokens for your records.'
      },
      domain: {
        read: 'Reads your domains, their terms and their nameservers.',
        write: 'Changes domain settings, for example nameservers or automatic renewal.',
        delete: 'Cancels domains in your account.',
        authcode: 'Requests auth codes for your domains.',
        transfer: 'Rejects requests to transfer your domains out of your account.',
        purchase: 'Registers new domains on your invoice.'
      },
      handle: {
        read: 'Reads your domain contacts and whether they are verified, with name, address, email address and phone number only for your own default contacts.',
        write: "Adds and changes domain contacts; for a contact that is the owner or admin contact of one of your domains, only its phone and fax numbers. Sends a contact a verification code by email or SMS, for example to owner{'@'}your-domain.com, and confirms it with the code you pass on.",
        delete: 'Deletes domain contacts from your account.'
      },
      hosting: {
        read: 'Reads your web hosting sites, their domains and their status.',
        write: 'Changes your web hosting sites, for example domains, PHP version or cron jobs.',
        delete: 'Deletes web hosting sites together with the data on them.',
        credentials: 'Creates SFTP and database credentials for your sites, reads existing database credentials and, together with "Web hosting: Read", downloads backups of your sites.',
        exec: 'Runs arbitrary commands inside your hosting container.',
        purchase: 'Books web hosting packages on your invoice.'
      },
      k8s: {
        read: 'Reads your namespaces, the workloads in them and their usage.',
        write: 'Creates, changes and removes resources in your namespaces.',
        credentials: 'Creates kubeconfigs for your namespaces.',
        purchase: 'Books additional Kubernetes quota on your invoice.'
      },
      mail: {
        read: "Reads your mailboxes and their settings, for example info{'@'}your-domain.com.",
        write: "Creates and changes mailboxes, aliases, forwards, vacation replies and the catch-all, for example contact{'@'}your-domain.com, and can switch addresses off. A forward may point outside your domains, so email sent to you can be passed on to any address.",
        delete: 'Deletes mailboxes together with every email in them, as well as aliases and forwards, and switches off the catch-all.',
        credentials: 'Resets the passwords of your mailboxes.',
        purchase: 'Books email packages on your invoice.'
      },
      registry: {
        read: 'Reads your container registry and the images in it.',
        write: 'Pushes images to your registry and overwrites existing ones.',
        delete: 'Deletes images and whole repositories from your registry.',
        credentials: 'Creates credentials for your registry.',
        purchase: 'Books additional registry quota on your invoice.'
      },
      storage: {
        read: 'Reads your buckets and the objects in them.',
        write: 'Writes objects to your buckets and overwrites existing ones.',
        delete: 'Deletes objects and whole buckets.',
        credentials: 'Creates S3 access keys for your buckets.',
        purchase: 'Books additional storage on your invoice.'
      },
      vserver: {
        read: 'Reads your VServers, their status and their usage.',
        write: 'Starts, stops and reconfigures your VServers.',
        delete: 'Deletes VServers together with their disks.',
        credentials: 'Sets root passwords and installs SSH keys on your VServers.',
        console: 'Opens the rescue console and types in it as if at an attached keyboard.',
        purchase: 'Orders new VServers on your invoice.'
      }
    },
    consequences: {
      delete: 'Nothing brings deleted data back, not even revoking this access afterwards.',
      purchase: 'This costs real money and is charged to your stored payment method.',
      credentials: 'Credentials that have been issued keep working after you revoke this access. You have to withdraw them one by one.',
      exec: 'Anything able to run commands reaches everything that lives on the container.',
      console: 'The console exposes the running system in full, independently of this access.',
      authcode: 'Anyone holding an auth code can move the domain to another registrar.',
      transfer: 'A rejected transfer has to be requested again.'
    },
    expiry: {
      sectionTitle: 'How long should this access last?',
      hint: 'After that the application has to ask you again. You can revoke it at any point before then.',
      days7: '7 days',
      days30: '30 days',
      days90: '90 days',
      never: 'No expiry',
      neverHint: 'No expiry means: until you revoke it yourself.'
    },
    spend: {
      sectionTitle: 'Spend cap',
      label: 'Cap in euros',
      hint: 'The total this application may charge to your invoice. Zero means nothing at all.',
      zeroNote: 'At zero the application buys nothing. That is the default.',
      confirm: 'I understand that this application may charge up to {amount} to my invoice.',
      invalid: 'Enter an amount of 0 or more.'
    },
    transfer: {
      sectionTitle: 'Disclosure and third countries',
      recipients: 'Whatever this application retrieves with the permissions you allow, we send to it. Where it goes from there is decided by the application and its settings: to its vendor, to the provider of the AI model, to both, or to nobody if everything runs on your own devices. We cannot tell.',
      policyUri: 'The application\'s privacy policy:',
      tosUri: 'The application\'s terms:',
      urisHint: 'Both addresses are stated by the application itself and are not checked by us; who receives the data and in which country is for you to read there.',
      basis: 'If a recipient sits outside the EEA, for example in the USA, the following applies: we have no contract with it and no safeguards under Art. 46 GDPR; whether an adequacy decision of the European Commission applies to its country depends on the application. Without such a decision we base the transfer of your data on your explicit consent (Art. 49(1)(a) GDPR).',
      risks: 'The risks: in that country there may be no supervisory authority, no processing principles equivalent to the GDPR and no enforceable data subject rights, and we have no influence over the processing there.',
      scope: 'The consent covers your own data. Other people\'s data reaches the recipient only in part: of other people\'s domain contacts only the identifier, type, country, verification status and, for organisations, the company name, and under mail permissions for example mailbox names; check that you are allowed to do that.',
      withdraw: 'You can withdraw the consent at any time by disconnecting the application under "Connected applications". Withdrawing does not affect the lawfulness of what was transferred until then, and it does not recall data already transferred.',
      details: 'Details are in the privacy policy under {section}. We keep the time, IP address and browser identifier of your decision as evidence.',
      detailsLink: '"Connected applications and AI agents"',
      confirm: 'Insofar as the data this application retrieves reaches a recipient outside the EEA, I expressly consent to that transfer. I have taken note of the risks.'
    },
    avv: {
      sectionTitle: 'Accept the data processing agreement first',
      body: 'Your account holds products for which we process personal data on your behalf, such as web hosting or email. Before an application gets access, the current data processing agreement (DPA, Art. 28 GDPR) has to be accepted. Read it, accept it, then allow access again. This request stays open until the time shown above.',
      openPdf: 'Open the agreement in a new tab',
      pdfUnavailable: 'The agreement could not be displayed. You can also read it under "DPA" in the control panel.',
      accepted: 'Agreement accepted. You can allow access now.'
    },
    actions: {
      approve: 'Allow access',
      deny: 'Decline',
      working: 'One moment'
    },
    errors: {
      missingRequest: 'This page is missing the request id. Start the connection again from the application.',
      unusable: 'This request has expired or was already answered. Start the connection again from the application.',
      accountHolder: 'Only the account holder can make this decision, in their own session. It cannot be done from an impersonated or switched session.',
      loadFailed: 'The request could not be loaded.',
      rateLimited: 'Too many attempts in a short time. Wait a minute, then try again.',
      submitFailed: 'Your decision could not be saved. Try again.',
      decisionRecorded: 'Your decision was saved, but the application could not be sent back to. Return to it and start the connection again. You can see and withdraw the access at any time under "Connected applications".',
      agentAccess: 'Connecting applications is not enabled for your account yet. Contact ITSH support if you would like to use it.',
      avvUnavailable: 'We could not check your data processing agreement just now. Try again in a moment.',
      toConnections: 'To your connected applications',
      retry: 'Try again',
      toDashboard: 'To the control panel'
    }
  }
} as const
